import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import OnboardingTooltip from '../components/OnboardingTooltip';
import DriverNotificationBell from '../components/DriverNotificationBell';
import DeliveryMapModal from '../components/DeliveryMapModal';
import { 
  Truck, Package, MapPin, Clock, CheckCircle, 
  LogOut, RefreshCw, DollarSign, Star, 
  Navigation, Phone, User, Loader2, Play,
  CheckCircle2, XCircle, Route, Wallet,
  Store, CreditCard, ArrowRight, Building2, Map, Camera
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Onboarding steps for delivery drivers
const driverOnboardingSteps = [
  {
    icon: Truck,
    title: "Bem-vindo, Entregador!",
    message: "Este é o seu painel para gerir entregas de lubrificantes Sonangol. Ganhe dinheiro fazendo entregas na sua área!",
    tips: "Mantenha o seu estado 'Disponível' para receber novos pedidos.",
    bgColor: "bg-gradient-to-r from-green-600 to-green-500"
  },
  {
    icon: Package,
    title: "Pedidos Disponíveis",
    message: "Veja os pedidos prontos para entrega na sua área. Cada card mostra o endereço, produtos e valor total.",
    tips: "Pedidos aparecem quando o funcionário marca como 'Pronto'.",
    bgColor: "bg-gradient-to-r from-blue-600 to-blue-500"
  },
  {
    icon: Play,
    title: "Aceitar Entrega",
    message: "Toque em 'Aceitar Entrega' para reservar um pedido. O seu estado mudará para 'Em Entrega'.",
    tips: "Só pode ter uma entrega activa de cada vez.",
    bgColor: "bg-gradient-to-r from-purple-600 to-purple-500"
  },
  {
    icon: Route,
    title: "Efectuar Entrega",
    message: "Dirija-se à estação para recolher o pedido, depois entregue no endereço indicado.",
    tips: "Confirme a entrega clicando em 'Confirmar Entrega' quando terminar.",
    bgColor: "bg-gradient-to-r from-amber-500 to-orange-500"
  },
  {
    icon: Star,
    title: "Seu Desempenho",
    message: "Acumule entregas para melhorar a sua avaliação. Entregas rápidas e sem problemas aumentam a sua reputação!",
    tips: "Veja o seu histórico e estatísticas no menu inferior.",
    bgColor: "bg-gradient-to-r from-yellow-500 to-amber-500"
  }
];

const getToken = () => localStorage.getItem('halubri_driver_token');

const api = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${getToken()}` }
});

const DeliveryDashboard = () => {
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('available');
  const [processingOrder, setProcessingOrder] = useState(null);
  const [mapModalOrder, setMapModalOrder] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const profileInputRef = useRef(null);


  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await api().patch(`/driver/profile-photo?photo_url=${encodeURIComponent(uploadRes.data.url)}`);
      setDriver({ ...driver, profile_photo: uploadRes.data.url });
      toast.success('Foto de perfil actualizada');
    } catch (err) {
      toast.error('Erro ao actualizar foto');
    }
    setUploadingPhoto(false);
  };


  const loadData = useCallback(async () => {
    try {
      const [driverRes, ordersRes, deliveriesRes] = await Promise.all([
        api().get('/driver/me'),
        api().get('/driver/available-orders'),
        api().get('/driver/my-deliveries')
      ]);
      setDriver(driverRes.data);
      setAvailableOrders(ordersRes.data);
      setMyDeliveries(deliveriesRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('halubri_driver_token');
        navigate('/delivery');
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate('/delivery');
      return;
    }
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [navigate, loadData]);

  const updateStatus = async (status) => {
    try {
      await api().patch(`/driver/status?status=${status}`);
      setDriver({ ...driver, status });
      toast.success(status === 'available' ? 'Está agora disponível!' : 'Estado actualizado');
    } catch (err) {
      toast.error('Erro ao actualizar estado');
    }
  };

  const acceptOrder = async (orderId) => {
    setProcessingOrder(orderId);
    try {
      await api().post(`/driver/accept-order/${orderId}`);
      toast.success('Pedido aceite!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aceitar pedido');
    }
    setProcessingOrder(null);
  };

  const completeDelivery = async (orderId) => {
    setProcessingOrder(orderId);
    try {
      await api().post(`/driver/complete-delivery/${orderId}`);
      toast.success('Entrega concluída!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao concluir entrega');
    }
    setProcessingOrder(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('halubri_driver_token');
    navigate('/delivery');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-AO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">A carregar...</p>
        </div>
      </div>
    );
  }

  const currentDelivery = myDeliveries.find(d => d.status !== 'delivered');

  return (
    <div className="min-h-screen bg-zinc-900 pb-20">
      {/* Onboarding Tooltips for first-time drivers */}
      <OnboardingTooltip 
        storageKey="halubri_driver_onboarding_seen"
        steps={driverOnboardingSteps}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 pt-6 pb-8 px-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Profile Photo with Upload */}
            <div className="relative">
              <input 
                type="file" 
                accept="image/*" 
                ref={profileInputRef}
                onChange={handleProfilePhotoChange}
                className="hidden"
              />
              {driver?.profile_photo ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${driver.profile_photo}`} 
                  alt={driver?.name}
                  className="w-12 h-12 rounded-xl object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
              )}
              <button
                onClick={() => profileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-3 h-3 text-green-600 animate-spin" />
                ) : (
                  <Camera className="w-3 h-3 text-green-600" />
                )}
              </button>
            </div>
            <div>
              <p className="text-white/80 text-sm">Olá,</p>
              <h1 className="text-white font-barlow font-bold text-xl">{driver?.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DriverNotificationBell />
            <Button variant="ghost" size="sm" onClick={loadData} className="text-white/80 hover:text-white hover:bg-white/10">
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/80 hover:text-white hover:bg-white/10">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Driver Info Card */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-xs">ID: {driver?.driver_id}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`${
                  driver?.driver_type === 'corporate' ? 'bg-sonangol-orange' : 'bg-green-600'
                } text-white border-0`}>
                  {driver?.driver_type === 'corporate' ? 'Corporate' : 'Individual'}
                </Badge>
                <Badge className="bg-white/20 text-white border-0">
                  {driver?.vehicle_type === 'moto' ? 'Moto' : 
                   driver?.vehicle_type === 'car' ? 'Carro' : 
                   driver?.vehicle_type === 'van' ? 'Van' : 'Camião'}
                </Badge>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-yellow-400">
                <Star className="w-4 h-4 fill-current" />
                <span className="font-bold">{driver?.rating?.toFixed(1)}</span>
              </div>
              <p className="text-white/60 text-xs">{driver?.total_deliveries} entregas</p>
              <p className="text-green-300 text-xs font-bold">{(driver?.total_earnings || 0).toLocaleString()} AOA</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Toggle */}
      <div className="px-4 -mt-4">
        <div className="bg-zinc-800 rounded-xl p-3 border border-zinc-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                driver?.status === 'available' ? 'bg-green-500 animate-pulse' : 
                driver?.status === 'busy' ? 'bg-yellow-500' : 'bg-zinc-500'
              }`} />
              <span className="text-white font-medium">
                {driver?.status === 'available' ? 'Disponível' : 
                 driver?.status === 'busy' ? 'Em Entrega' : 'Offline'}
              </span>
            </div>
            <div className="flex gap-2">
              {driver?.status !== 'available' && (
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => updateStatus('available')}
                >
                  Ficar Disponível
                </Button>
              )}
              {driver?.status === 'available' && (
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border-zinc-600 text-zinc-300"
                  onClick={() => updateStatus('offline')}
                >
                  Ficar Offline
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Current Delivery Banner */}
      {currentDelivery && (
        <div className="px-4 mt-4">
          <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Navigation className="w-5 h-5 text-white" />
              <span className="text-white font-bold">Entrega em Curso</span>
            </div>
            <p className="text-white/90 text-sm font-medium mb-2">
              #{currentDelivery.order_id?.slice(0, 8)} · {currentDelivery.customer_name || 'Cliente'}
            </p>
            {currentDelivery.customer_phone && (
              <a href={`tel:${currentDelivery.customer_phone}`} className="tap-target flex items-center gap-1 text-white/80 text-sm mb-2 hover:text-white">
                <Phone className="w-3 h-3" />
                {currentDelivery.customer_phone}
              </a>
            )}
            
            {/* Pickup info */}
            {currentDelivery.pickup_station && (
              <div className="bg-white/10 rounded-lg p-2 mb-2">
                <p className="text-white/70 text-xs flex items-center gap-1">
                  <Store className="w-3 h-3" />
                  Recolha:
                </p>
                <p className="text-white text-sm font-medium">{currentDelivery.pickup_station}</p>
              </div>
            )}
            
            {/* Delivery address */}
            <div className="bg-white/10 rounded-lg p-2 mb-2">
              <p className="text-white/70 text-xs flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Entregar em:
              </p>
              <p className="text-white text-sm font-medium">{currentDelivery.delivery_address || 'Endereço não disponível'}</p>
            </div>
            
            {/* Payment method */}
            {currentDelivery.payment_method && (
              <div className="bg-white/10 rounded-lg p-2 mb-3">
                <p className="text-white/70 text-xs flex items-center gap-1">
                  <CreditCard className="w-3 h-3" />
                  Pagamento:
                </p>
                <p className="text-white text-sm font-medium">{currentDelivery.payment_method}</p>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1 bg-white/20 text-white border-white/40 hover:bg-white/30 font-bold"
                onClick={() => setMapModalOrder({
                  id: currentDelivery.order_id,
                  pickup_station_name: currentDelivery.pickup_station,
                  pickup_station_address: currentDelivery.pickup_address,
                  delivery_address: currentDelivery.delivery_address,
                  total_aoa: currentDelivery.order_total
                })}
                data-testid="view-map-current-delivery"
              >
                <Map className="w-4 h-4 mr-2" />
                Ver Mapa
              </Button>
              <Button 
                className="flex-1 bg-white text-yellow-600 hover:bg-white/90 font-bold"
                onClick={() => completeDelivery(currentDelivery.order_id)}
                disabled={processingOrder === currentDelivery.order_id}
                data-testid="complete-delivery-btn"
              >
                {processingOrder === currentDelivery.order_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirmar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="px-4 mt-4">
        <div className="bg-zinc-800 p-1 rounded-xl flex gap-1">
          <button
            onClick={() => setActiveView('available')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              activeView === 'available' ? 'bg-green-600 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            Disponíveis ({availableOrders.length})
          </button>
          <button
            onClick={() => setActiveView('history')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              activeView === 'history' ? 'bg-green-600 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            Histórico
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 mt-4 space-y-3">
        {activeView === 'available' ? (
          <>
            {availableOrders.length === 0 ? (
              <div className="bg-zinc-800 rounded-xl p-8 text-center border border-zinc-700">
                <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-white font-medium">Nenhum pedido disponível</p>
                <p className="text-zinc-500 text-sm">Novos pedidos aparecerão aqui</p>
              </div>
            ) : (
              availableOrders.map((order) => (
                <div key={order.id} className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
                  <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
                    <div>
                      <span className="font-barlow font-bold text-white">#{order.id.slice(0, 8)}</span>
                      {order.is_corporate_order && (
                        <Badge className="ml-2 bg-purple-600 text-white border-0 text-xs">Corporate</Badge>
                      )}
                    </div>
                    <span className="font-barlow font-bold text-green-500">
                      {order.total_aoa?.toLocaleString('pt-AO')} AOA
                    </span>
                  </div>
                  
                  <div className="px-4 py-3">
                    {/* Customer Info */}
                    <div className="flex items-center gap-2 text-sm text-white mb-2">
                      <User className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="font-medium">{order.customer_name || 'Cliente'}</span>
                    </div>
                    {order.customer_phone && (
                      <div className="flex items-center gap-2 text-sm text-zinc-300 mb-2">
                        <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <a href={`tel:${order.customer_phone}`} className="tap-target hover:text-white transition-colors">
                          {order.customer_phone}
                        </a>
                      </div>
                    )}
                    
                    {/* Pickup Station Info - NEW */}
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 text-sm text-green-400 font-medium mb-1">
                        <Store className="w-4 h-4 flex-shrink-0" />
                        <span>RECOLHA NA ESTAÇÃO</span>
                      </div>
                      <p className="text-white font-semibold text-sm ml-6">{order.pickup_station_name || order.station_name}</p>
                      <p className="text-zinc-400 text-xs ml-6 mt-1">{order.pickup_station_address || 'Endereço não disponível'}</p>
                      {order.pickup_station_phone && (
                        <a href={`tel:${order.pickup_station_phone}`} className="tap-target text-green-400 text-xs ml-6 flex items-center gap-1 mt-1 hover:text-green-300">
                          <Phone className="w-3 h-3" />
                          {order.pickup_station_phone}
                        </a>
                      )}
                    </div>
                    
                    {/* Delivery Address - NEW */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 text-sm text-blue-400 font-medium mb-1">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span>ENTREGAR EM</span>
                      </div>
                      <p className="text-white text-sm ml-6">{order.delivery_address || order.address || 'Endereço não especificado'}</p>
                    </div>
                    
                    {/* Payment Method - NEW */}
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 text-sm text-yellow-400 font-medium">
                        <CreditCard className="w-4 h-4 flex-shrink-0" />
                        <span>PAGAMENTO:</span>
                        <span className="text-white font-semibold">{order.payment_method_label || (
                          order.payment_method === 'multicaixa' ? 'Multicaixa Express' :
                          order.payment_method === 'mobile_money' ? 'Mobile Money' :
                          order.payment_method === 'cash' ? 'Dinheiro' :
                          order.payment_method || 'Não especificado'
                        )}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                      <Clock className="w-4 h-4" />
                      <span>{formatDate(order.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="px-4 py-3 bg-zinc-900/50">
                    <p className="text-zinc-400 text-xs mb-1">Produtos:</p>
                    <div className="space-y-1">
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <p key={idx} className="text-zinc-300 text-sm">{item.quantity}x {item.name}</p>
                      ))}
                      {order.items?.length > 3 && (
                        <p className="text-zinc-500 text-xs">+ {order.items.length - 3} mais...</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="px-4 py-3 border-t border-zinc-700 flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1 border-sonangol-orange text-sonangol-orange hover:bg-sonangol-orange/10 font-barlow font-bold"
                      onClick={() => setMapModalOrder(order)}
                      data-testid={`view-map-${order.id}`}
                    >
                      <Map className="w-4 h-4 mr-2" />
                      Ver Mapa
                    </Button>
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700 font-barlow font-bold"
                      onClick={() => acceptOrder(order.id)}
                      disabled={processingOrder === order.id || driver?.status === 'busy'}
                      data-testid={`accept-order-${order.id}`}
                    >
                      {processingOrder === order.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Aceitar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </>
        ) : (
          <>
            {myDeliveries.length === 0 ? (
              <div className="bg-zinc-800 rounded-xl p-8 text-center border border-zinc-700">
                <Truck className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-white font-medium">Sem histórico</p>
                <p className="text-zinc-500 text-sm">As suas entregas aparecerão aqui</p>
              </div>
            ) : (
              myDeliveries.map((delivery) => (
                <div key={delivery.id} className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-barlow font-bold text-white">#{delivery.order_id?.slice(0, 8)}</span>
                    <Badge className={`${
                      delivery.status === 'delivered' ? 'bg-green-600' : 'bg-yellow-500'
                    } text-white border-0`}>
                      {delivery.status === 'delivered' ? 'Entregue' : 'Em Curso'}
                    </Badge>
                  </div>
                  <p className="text-zinc-400 text-sm">{delivery.delivery_address || 'Endereço não disponível'}</p>
                  <div className="flex items-center justify-between mt-2 text-xs text-zinc-500">
                    <span>{formatDate(delivery.assigned_at)}</span>
                    {delivery.order_total && (
                      <span className="text-green-500 font-medium">{delivery.order_total.toLocaleString('pt-AO')} AOA</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 safe-bottom bg-zinc-800 border-t border-zinc-700 flex items-center justify-around z-50">
        <button
          onClick={() => setActiveView('available')}
          className={`flex flex-col items-center justify-center py-2 px-4 ${
            activeView === 'available' ? 'text-green-500' : 'text-zinc-500'
          }`}
        >
          <Package className="w-5 h-5" />
          <span className="text-[10px] mt-1">Pedidos</span>
        </button>
        <button
          onClick={() => setActiveView('history')}
          className={`flex flex-col items-center justify-center py-2 px-4 ${
            activeView === 'history' ? 'text-green-500' : 'text-zinc-500'
          }`}
        >
          <Clock className="w-5 h-5" />
          <span className="text-[10px] mt-1">Histórico</span>
        </button>
        <button
          onClick={handleLogout}
          className="flex flex-col items-center justify-center py-2 px-4 text-red-400"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-[10px] mt-1">Sair</span>
        </button>
      </nav>

      {/* Map Modal */}
      <DeliveryMapModal
        isOpen={!!mapModalOrder}
        onClose={() => setMapModalOrder(null)}
        order={mapModalOrder}
      />
    </div>
  );
};

export default DeliveryDashboard;
